import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { SessionSetting } from '../model/session-setting';
import { UserService } from '../service/user.service';

@Injectable({
  providedIn: 'root'
})
export class SettingNavService
{
  private sessionSetting: SessionSetting;
  private currentUserRole: string;
  private items: Array<{ titleKey: string; descriptionKey: string; permissions: string[]; route: string }> = [];

  constructor(
    private permissionsService: NgxPermissionsService,
    private userService: UserService)
  {
    this.items = [];
  }

  public getMenuItems(communityEnabled: boolean, patientLabel: string, addUpdateCommunityResourcesEnabled: boolean,
    providersEnabled: boolean, sessionSetting: SessionSetting, currentUserRole: string): Array<{ titleKey: string; descriptionKey: string; permissions: string[]; route: string }>
  {
    this.sessionSetting = sessionSetting;
    this.currentUserRole = currentUserRole;
    this.items = [];

    this.addSettingItem(
      'ACCUMULATORS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-ACCUMULATORS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'accumulators', 'fa-layer-plus');

    this.addSettingItem(
      'ANALYTICS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-ANALYTICS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'analytics/admin', 'fa-analytics');

    this.addSettingItem(
      'ASSESSMENTS',
      'SETTING-GROUP-2-ASSESSMENTS-AND-TEMPLATES',
      'SETTING-DESCRIPTION-ASSESSMENT',
      ['ROLE_CARE_COORDINATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD', 'ROLE_COMMUNITY_ADMIN'],
      'assessments', 'fa-clipboard-list');

    this.addSettingItem(
      'ASSESSMENT-TYPES',
      'SETTING-GROUP-2-ASSESSMENTS-AND-TEMPLATES',
      'SETTING-DESCRIPTION-ASSESSMENT-TYPES',
      ['ROLE_CARE_COORDINATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'assessment-types', 'fa-cog');

    this.addSettingItem(
      'BILLABLE-SERVICES',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-BILLABLE-SERVICES',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'billable-services', 'fa-file-invoice-dollar');

    this.addSettingItem(
      'CHANNEL-CATEGORIES',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-CHANNEL-CATEGORIES',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'channel-categories', 'fa-cog');

    this.addSettingItem(
      'CLINICAL-MESSAGES',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-CLINICAL-MESSAGES',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'clinical-messages', 'fa-inbox-in');

    if (addUpdateCommunityResourcesEnabled)
    {
      this.addSettingItem(
        'COMMUNITY-PROVIDER-PROGRAM',
        'SETTING-GROUP-3-PROVIDER-AND-PATIENT',
        'SETTING-DESCRIPTION-PROVIDER-PROGRAMS',
        ['ROLE_CARE_COORDINATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD', 'ROLE_COMMUNITY_ADMIN', 'ROLE_DIRECTORY_MANAGER'],
        'community-services', 'fa-cog');
    }

    this.addSettingItem(
      'CODE-SETS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-CODE-SETS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'dictionaries', 'fa-books');

    this.addSettingItem(
      'EMAIL-TEMPLATES',
      'SETTING-GROUP-2-ASSESSMENTS-AND-TEMPLATES',
      'SETTING-DESCRIPTION-EMAIL-TEMPLATES',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'templates', 'fa-at');

    this.addSettingItem(
      'EXTENSIONS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-EXTENSIONS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'extensions', 'fa-tag');

    this.addSettingItem(
      'FIELD-CONFIGURATIONS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-FIELD-CONFIGURATIONS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'field-configurations', 'fa-cog');

    this.addSettingItem(
      'GROUPS',
      'SETTING-GROUP-4-USER',
      'SETTING-DESCRIPTION-GROUPS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_COMMUNITY_COORDINATOR', 'ROLE_PROGRAM_LEAD'],
      'groups', 'fa-layer-group');

    this.addSettingItem(
      'ADMIN-HELP-DOCUMENTS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-HELP-DOCUMENT',
      ['ROLE_OPERATOR'],
      'help-documents', 'fa-cog');

    if (this.currentUserRole === 'ROLE_REVENUE_MANAGER_OPERATOR')
    {
      this.addSettingItem(
        'PAYERS',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-PAYERS',
        ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_REVENUE_MANAGER_OPERATOR'],
        'insurers', 'fa-cog');
    }
    else
    {
      this.addSettingItem(
        'INSURERS',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-INSURERS',
        ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_REVENUE_MANAGER_OPERATOR'],
        'insurers', 'fa-cog');
    }

    this.addSettingItem(
      'INTEGRITY-TOOLS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-INTEGRITY-TOOLS',
      ['ROLE_OPERATOR'],
      'integrity-tools', 'fa-toolbox');

    this.addSettingItem(
      'ISSUERS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-ISSUERS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'issuers', 'fa-cog');

    this.addSettingItem(
      'PROGRAM-METRICS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-PROGRAM-METRICS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'metrics', 'fa-cog');

    this.addSettingItem(
      'NOTICES',
      'SETTING-GROUP-4-USER',
      'SETTING-DESCRIPTION-NOTICES',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'notices', 'fa-alarm-exclamation');

    if (this.currentUserRole === 'ROLE_REVENUE_MANAGER_OPERATOR')
    {
      this.addSettingItem(
        'ORGANIZATIONS',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-ORGANIZATIONS',
        ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_REVENUE_MANAGER_OPERATOR'],
        'organizations', 'fa-hospital-user');
    }
    else
    {
      this.addSettingItem(
        'ORGANIZATIONS',
        'SETTING-GROUP-4-USER',
        'SETTING-DESCRIPTION-ORGANIZATIONS',
        ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
        'organizations', 'fa-hospital-user');
    }

    this.addSettingItem(
      'PATIENT-IMPORT',
      'SETTING-GROUP-3-PROVIDER-AND-PATIENT',
      'SETTING-DESCRIPTION-PATIENT-IMPORT',
      ['ROLE_CARE_COORDINATOR', 'ROLE_PROGRAM_LEAD', 'ROLE_OPERATOR'],
      'patients-import', 'fa-users', patientLabel);

    this.addSettingItem(
      'PATIENT-MERGE',
      'SETTING-GROUP-3-PROVIDER-AND-PATIENT',
      'SETTING-DESCRIPTION-PATIENT-MERGE',
      ['ROLE_OPERATOR'],
      'patients-merge', 'fa-code-merge', patientLabel);

    this.addSettingItem(
      'PROGRAM-CATEGORIES',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-PROGRAM-CATEGORIES',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'program-categories', 'fa-cog');

    this.addSettingItem(
      'PROGRAM-TEMPLATES',
      'SETTING-GROUP-2-ASSESSMENTS-AND-TEMPLATES',
      'SETTING-DESCRIPTION-TEMPLATES',
      ['ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'program-templates', 'fa-cube');

    this.addSettingItem(
      'FACILITIES',
      'SETTING-GROUP-3-PROVIDER-AND-PATIENT',
      'SETTING-DESCRIPTION-FACILITIES',
      ['ROLE_CARE_COORDINATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'facilities', 'fa-clinic-medical');

    if (providersEnabled)
    {
      this.addSettingItem(
        'PROVIDER-DIRECTORY',
        'SETTING-GROUP-3-PROVIDER-AND-PATIENT',
        'SETTING-DESCRIPTION-PROVIDER-DIRECTORY',
        ['ROLE_CARE_COORDINATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD', 'ROLE_DIRECTORY_MANAGER'],
        'providers', 'fa-users-medical');
    }

    if (communityEnabled)
    {
      this.addSettingItem(
        'COMMUNITY-SERVICE-CATEGORIES',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-COMMUNITY-SERVICE-CATEGORIES',
        ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
        'community-service-categories', 'fa-cog');
    }

    if (sessionSetting.workflowLogEnabled || sessionSetting.serviceLogEnabled)
    {
      this.addSettingItem(
        'SYSTEM-LOGS',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-SYSTEM-LOGS',
        ['ROLE_OPERATOR'],
        'system-logs', 'fa-server');
    }

    this.addSettingItem(
      'SYSTEM-NOTES',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-SYSTEM-NOTES',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'system-notes', 'fa-comment-alt-lines');

    this.addSettingItem(
      'SYSTEM-CONFIGURATIONS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-SYSTEM-CONFIGURATIONS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'system-settings', 'fa-cogs');

    this.addSettingItem(
      'TEAM-ROLES',
      'SETTING-GROUP-4-USER',
      'SETTING-DESCRIPTION-TEAM-ROLES',
      ['ROLE_OPERATOR', 'ROLE_PROGRAM_LEAD'],
      'team-roles', 'fa-users-class');

    if (communityEnabled)
    {
      this.addSettingItem(
        'TERMS-AND-CONDITIONS',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-TERMS-AND-CONDITIONS',
        ['ROLE_PROGRAM_LEAD', 'ROLE_OPERATOR'],
        'terms-and-conditions', 'fa-user-shield');
    }

    if (this.currentUserRole === 'ROLE_REVENUE_MANAGER_OPERATOR')
    {
      this.addSettingItem(
        'USERS',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-USERS-REVENUE-MAMNAGER',
        ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_COMMUNITY_ADMIN', 'ROLE_PROGRAM_LEAD', 'ROLE_REVENUE_MANAGER_OPERATOR'],
        'users', 'fa-users-cog');
    }
    else
    {
      this.addSettingItem(
        'USERS',
        'SETTING-GROUP-4-USER',
        'SETTING-DESCRIPTION-USERS',
        ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR', 'ROLE_COMMUNITY_ADMIN', 'ROLE_PROGRAM_LEAD', 'ROLE_REVENUE_MANAGER_OPERATOR'],
        'users', 'fa-users-cog');
    }

    this.addSettingItem(
      'VIDEO-PROVIDERS',
      'SETTING-GROUP-1-SYSTEM',
      'SETTING-DESCRIPTION-VIDEO-PROVIDERS',
      ['ROLE_SYSTEM_OPERATOR', 'ROLE_OPERATOR'],
      'video-providers', 'fa-video');

    if (this.currentUserRole === 'ROLE_REVENUE_MANAGER_OPERATOR')
    {
      this.addSettingItem(
        'CBO',
        'SETTING-GROUP-1-SYSTEM',
        'SETTING-DESCRIPTION-CBO',
        ['ROLE_REVENUE_MANAGER_OPERATOR'],
        'cbo', 'fa-books');
    }

    return this.items;
  }

  private addSettingItem(titleKey: string, groupKey: string, descriptionKey: string,
    permissions: string[], route: string, icon: string = null,
    patientLabel: string = null): void
  {
    this.permissionsService.hasPermission(permissions).then((response: any) =>
    {
      // Add to items list only if permission is allowed
      if (response)
      {
        // Check if program lead have access to groups
        if (titleKey === 'GROUPS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD' || this.currentUserRole === 'ROLE_COMMUNITY_COORDINATOR' ||
          this.currentUserRole === 'ROLE_OPERATOR'))
        {
          const isAllowOrganizationGroups = (this.sessionSetting.allowOrganizationGroupsRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.allowOrganizationGroupsRoles) : false;
          if (!isAllowOrganizationGroups)
          {
            return;   // Groups is not allowed, we should hide this option
          }
        }
        // Check if program lead have access to users
        if (titleKey === 'USERS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD' || this.currentUserRole === 'ROLE_OPERATOR'))
        {
          const isAllowOrganizationUsers = (this.sessionSetting.allowOrganizationUsersRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.allowOrganizationUsersRoles) : false;
          if (!isAllowOrganizationUsers)
          {
            return;   // Users is not allowed, we should hide this option
          }
        }
        if (titleKey === 'TEAM-ROLES' && this.currentUserRole === 'ROLE_PROGRAM_LEAD')
        {
          const isAddUpdateTeamRolesRoles = (this.sessionSetting.addUpdateTeamRolesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateTeamRolesRoles) : false;
          if (!isAddUpdateTeamRolesRoles)
          {
            return;   // Team Roles is not allowed, we should hide this option
          }
        }

        if (titleKey === 'ASSESSMENT-TYPES' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateAssessmentTypesRoles = (this.sessionSetting.addUpdateAssessmentTypesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateAssessmentTypesRoles) : false;
          if (!isAddUpdateAssessmentTypesRoles)
          {
            return;   // Assessment Types is not allowed, we should hide this option
          }
        }
        if (titleKey === 'ASSESSMENTS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateAssessmentsRoles = (this.sessionSetting.addUpdateAssessmentsRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateAssessmentsRoles) : false;
          if (!isAddUpdateAssessmentsRoles)
          {
            return;   // Assessments is not allowed, we should hide this option
          }
        }
        if (titleKey === 'PROGRAM-TEMPLATES' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateProgramTemplatesRoles = (this.sessionSetting.addUpdateProgramTemplatesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateProgramTemplatesRoles) : false;
          if (!isAddUpdateProgramTemplatesRoles)
          {
            return;   // Program Templates is not allowed, we should hide this option
          }
        }

        if (titleKey === 'FACILITIES' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateFacilitiesRoles = (this.sessionSetting.addUpdateFacilitiesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateFacilitiesRoles) : false;
          if (!isAddUpdateFacilitiesRoles)
          {
            return;   // Facilities is not allowed, we should hide this option
          }
        }
        if (titleKey === 'PROVIDER-DIRECTORY')
        {
          if (this.currentUserRole !== 'ROLE_PROGRAM_LEAD')
          {
            const isProvidersRoles = (this.sessionSetting.providersRoles !== '') ?
              this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.providersRoles) : false;
            if (!isProvidersRoles)
            {
              return;   // Providers is not allowed, we should hide this option
            }
          }
          else
          {
            const isAddUpdateProvidersRoles = (this.sessionSetting.addUpdateProvidersRoles !== '') ?
              this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateProvidersRoles) : false;
            if (!isAddUpdateProvidersRoles)
            {
              return;   // Providers is not allowed, we should hide this option
            }
          }
        }
        if (titleKey === 'PATIENT-IMPORT' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateClientImportRoles = (this.sessionSetting.addUpdateClientImportRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateClientImportRoles) : false;
          if (!isAddUpdateClientImportRoles)
          {
            return;   // Client Import is not allowed, we should hide this option
          }
        }

        if (titleKey === 'CHANNEL-CATEGORIES' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateChannelCategoriesRoles = (this.sessionSetting.addUpdateChannelCategoriesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateChannelCategoriesRoles) : false;
          if (!isAddUpdateChannelCategoriesRoles)
          {
            return;   // Channel Categories is not allowed, we should hide this option
          }
        }
        if (titleKey === 'CODE-SETS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateDictionariesRoles = (this.sessionSetting.addUpdateDictionariesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateDictionariesRoles) : false;
          if (!isAddUpdateDictionariesRoles)
          {
            return;   // Dictionaries is not allowed, we should hide this option
          }
        }
        if (titleKey === 'EXTENSIONS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateExtensionsRoles = (this.sessionSetting.addUpdateExtensionsRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateExtensionsRoles) : false;
          if (!isAddUpdateExtensionsRoles)
          {
            return;   // Extensions is not allowed, we should hide this option
          }
        }
        if (titleKey === 'FIELD-CONFIGURATIONS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateFieldConfigurationsRoles = (this.sessionSetting.addUpdateFieldConfigurationsRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateFieldConfigurationsRoles) : false;
          if (!isAddUpdateFieldConfigurationsRoles)
          {
            return;   // Field Configurations is not allowed, we should hide this option
          }
        }
        if (titleKey === 'PROGRAM-METRICS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateMetricsRoles = (this.sessionSetting.addUpdateMetricsRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateMetricsRoles) : false;
          if (!isAddUpdateMetricsRoles)
          {
            return;   // Metrics is not allowed, we should hide this option
          }
        }
        if (titleKey === 'PROGRAM-CATEGORIES' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateProgramCategoriesRoles = (this.sessionSetting.addUpdateProgramCategoriesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateProgramCategoriesRoles) : false;
          if (!isAddUpdateProgramCategoriesRoles)
          {
            return;   // Program Categories is not allowed, we should hide this option
          }
        }
        if (titleKey === 'COMMUNITY-SERVICE-CATEGORIES' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateServiceCategoriesRoles = (this.sessionSetting.addUpdateServiceCategoriesRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateServiceCategoriesRoles) : false;
          if (!isAddUpdateServiceCategoriesRoles)
          {
            return;   // Service Categories is not allowed, we should hide this option
          }
        }
        if (titleKey === 'TERMS-AND-CONDITIONS' && (this.currentUserRole === 'ROLE_PROGRAM_LEAD'))
        {
          const isAddUpdateTermsAndConditionsRoles = (this.sessionSetting.addUpdateTermsAndConditionsRoles !== '') ?
            this.userService.isRoleInList(this.currentUserRole, this.sessionSetting.addUpdateTermsAndConditionsRoles) : false;
          if (!isAddUpdateTermsAndConditionsRoles)
          {
            return;   // Terms & Conditions is not allowed, we should hide this option
          }
        }
        const setting: any = {};
        setting.titleKey = titleKey;
        setting.groupKey = groupKey;
        setting.descriptionKey = descriptionKey;
        setting.permissions = permissions;
        setting.route = route;
        setting.icon = icon;
        const labelObject: any = {};
        labelObject.patientLabel = patientLabel;
        setting.patientLabel = labelObject;
        this.items.push(setting);
      }
    });
  }
}
